import type { ReactNode } from 'react';
import { cn } from '../lib/utils';

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
}

/**
 * DESIGN.md §6 Segmented control (`RangeSwitcher`). A group of toggle buttons
 * (`aria-pressed`) so it works with Tab/Enter/Space without custom key handling.
 * `size="lg"` uses touch-height items for the kitchen and cashier screens.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  size = 'md',
  scroll = false,
  className,
}: {
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  size?: 'md' | 'lg';
  /** Horizontal scrolling for long lists (category / station tabs). */
  scroll?: boolean;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex max-w-full rounded-lg border border-[var(--line)] p-0.5',
        scroll && 'overflow-x-auto',
        className,
      )}
    >
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(o.value)}
            className={cn(
              'shrink-0 rounded-md px-3 font-medium whitespace-nowrap transition-colors',
              size === 'md' ? 'h-8 text-xs' : 'h-10 text-sm',
              selected
                ? 'bg-[var(--accent-soft)] text-[var(--ink)]'
                : 'text-[var(--ink-soft)] hover:text-[var(--ink)]',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
